function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Component } from 'react';
import { SpecraButton } from '../specra/ensigns';
/**
 * Error boundary for page sections that catches render errors
 * and displays a consistent fallback UI.
 *
 * @example
 * <PageSectionErrorBoundary sectionName="Chat Messages">
 *   <ChatThread messages={messages} />
 * </PageSectionErrorBoundary>
 *
 * @example
 * // With custom fallback
 * <PageSectionErrorBoundary
 *   sectionName="Chat"
 *   fallback={<CustomErrorUI />}
 * >
 *   <ChatView />
 * </PageSectionErrorBoundary>
 */ export class PageSectionErrorBoundary extends Component {
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            error
        };
    }
    componentDidCatch(error, errorInfo) {
        const sectionLabel = this.props.sectionName || 'Section';
        console.error(`[${sectionLabel}] Error:`, error, errorInfo);
        this.props.onError?.(error, errorInfo);
    }
    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                "aria-live": "polite",
                className: "flex flex-col items-center justify-center py-12 px-4 text-center",
                children: [
                    /*#__PURE__*/ _jsx(AlertTriangle, {
                        className: "h-10 w-10 text-amber-500 mb-3",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold mb-1",
                        children: "Something went wrong"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground mb-4 max-w-md",
                        children: this.props.sectionName ? `The ${this.props.sectionName} section encountered an error.` : 'This section encountered an error.'
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: this.handleRetry,
                        children: [
                            /*#__PURE__*/ _jsx(RefreshCw, {
                                className: "w-4 h-4 mr-2"
                            }),
                            "Try again"
                        ]
                    })
                ]
            });
        }
        return this.props.children;
    }
    constructor(...args){
        super(...args), _define_property(this, "state", {
            hasError: false,
            error: null
        }), _define_property(this, "handleRetry", ()=>{
            this.setState({
                hasError: false,
                error: null
            });
            this.props.onRetry?.();
        });
    }
}
